// V2 版式：grid-table（前端 DOM 版）——对照后端 v2/layouts/grid-table.ts 逐坐标移植
// 列头 ink 底 accentOn 文字；行头 grey1 底；格内 12pt 上对齐；marks 右上角 accent 方块+tag。

import { SPACING } from "../../lib/tokens.generated";
import { V2GEO } from "../../lib/v2geo.generated";
import { px, pt, titleSize, kickerOf, footOf } from "./shared";
import { isLuxe, luxeExt } from "./luxe";
import { reportTheme, memphisTheme, memphisPalette } from "./themes";
import V2Chrome from "./Chrome";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

export default function V2GridTable({ c, skin, page, total, editable, handlers }: V2LayoutProps) {
  const GEO = V2GEO["grid-table"] as any;
  const m = SPACING.marginX;
  const W = 13.333;
  const mag = skin.style === "magazine";
  // luxe：行头浅金 blush；列头墨黑底上压裸金文字（accentOn=墨黑会隐形——grid-table.ts headFg 分支）
  const luxe = isLuxe(skin);
  const ext = luxeExt(skin);
  const theme = skin.theme as { grey?: { grey1?: string; ink?: string }; ink?: string };
  const grey1 = theme.grey?.grey1 ?? (ext ? ext.blush : skin.hairline);
  const ink = theme.grey?.ink ?? theme.ink ?? skin.text;
  // report：列头纯黑（inkDeep）底 + 近白文字（研报表头）；luxe：墨黑底压裸金文字（grid-table.ts:52-53）
  const reportT = reportTheme(skin);
  const mpT = memphisTheme(skin);
  const headFg = reportT ? reportT.ink : luxe ? skin.accent : skin.accentOn;

  const bi = c.blocks.findIndex((x) => x.type === "table");
  const b = bi >= 0 ? c.blocks[bi] : null;
  if (!b || b.type !== "table") {
    return <V2Chrome skin={skin} kicker={kickerOf(page, "grid-table")} page={page} total={total} foot={footOf()} />;
  }

  const cols = b.colHeads.length;
  const rows = b.rowHeads.length;
  const bodyX = m + GEO.rowHeadW;
  const bodyW = W - m - bodyX;
  const cw = bodyW / cols;
  const rowH = Math.min(GEO.maxRowH, (GEO.tableBottom - GEO.tableTop - GEO.headH) / rows);

  return (
    <>
      <V2Chrome skin={skin} kicker={kickerOf(page, "grid-table")} page={page} total={total} foot={footOf()} />
      <EditableText
        value={c.title} path="title" editable={editable} multiline
        onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(m), top: px(GEO.titleY), width: px(W - 2 * m), height: px(GEO.titleH),
          fontSize: pt(titleSize(c.title, skin.scale.pageTitle)), color: skin.text,
          fontFamily: skin.fontHead, fontWeight: mag ? 700 : 400, lineHeight: 1.1,
        }}
      />
      {/* 列头行：memphis（043 §4.7）每列独立彩头带（5 色轮用，黄带黑字）；report 纯黑底；其余 ink 底 + accentOn 文字 */}
      {mpT ? (
        b.colHeads.map((h, i) => {
          const hc = memphisPalette(mpT)[i % 5];
          return (
            <div key={`h${i}`}>
              <div style={{ position: "absolute", left: px(bodyX + i * cw), top: px(GEO.tableTop), width: px(cw), height: px(GEO.headH), background: hc }} />
              <EditableText
                value={h} path={`blocks.${bi}.colHeads.${i}`} editable={editable}
                onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
                style={{
                  position: "absolute", left: px(bodyX + i * cw + GEO.cellPadX), top: px(GEO.tableTop),
                  width: px(cw - 2 * GEO.cellPadX), height: px(GEO.headH),
                  fontSize: pt(skin.scale.body), color: i % 5 === 4 ? mpT.ink : "#FFFFFF", fontFamily: skin.fontBody,
                  fontWeight: 700, display: "flex", alignItems: "center",
                }}
              />
            </div>
          );
        })
      ) : (
        <>
          <div style={{ position: "absolute", left: px(bodyX), top: px(GEO.tableTop), width: px(bodyW), height: px(GEO.headH), background: reportT ? reportT.inkDeep : ink }} />
          {b.colHeads.map((h, i) => (
            <EditableText
              key={`h${i}`} value={h} path={`blocks.${bi}.colHeads.${i}`} editable={editable}
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(bodyX + i * cw + GEO.cellPadX), top: px(GEO.tableTop),
                width: px(cw - 2 * GEO.cellPadX), height: px(GEO.headH),
                fontSize: pt(skin.scale.body), color: headFg, fontFamily: skin.fontBody,
                fontWeight: 700, display: "flex", alignItems: "center",
              }}
            />
          ))}
        </>
      )}
      {/* 数据行 */}
      {b.rowHeads.map((rh, r) => {
        const y = GEO.tableTop + GEO.headH + r * rowH;
        return (
          <div key={`r${r}`}>
            <div style={{ position: "absolute", left: px(m), top: px(y), width: px(GEO.rowHeadW - 0.12), height: px(rowH - 0.04), background: grey1 }} />
            {/* 045 #11：note 作静态 span 渲染在可编辑区外，防编辑把 `· note` 重复累积进 label */}
            <div style={{
              position: "absolute", left: px(m + GEO.cellPadX), top: px(y),
              width: px(GEO.rowHeadW - 0.12 - 2 * GEO.cellPadX), height: px(rowH - 0.04),
              fontSize: pt(rh.note ? skin.scale.meta : skin.scale.body), color: skin.text,
              fontFamily: skin.fontBody, fontWeight: 700, display: "flex", alignItems: "center",
            }}>
              <EditableText
                value={rh.label}
                path={`blocks.${bi}.rowHeads.${r}.label`} editable={editable}
                onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
                style={{ flex: "0 1 auto", minWidth: 0, display: "flex", alignItems: "center" }}
              />
              {rh.note && <span style={{ flexShrink: 0 }}>{` · ${rh.note}`}</span>}
            </div>
            <div style={{ position: "absolute", left: px(bodyX), top: px(y + rowH - 0.0104), width: px(bodyW), height: 1, background: skin.hairline }} />
            {Array.from({ length: cols }, (_, col) => (
              <EditableText
                key={`c${col}`} value={b.cells[r]?.[col] ?? ""} path={`blocks.${bi}.cells.${r}.${col}`} editable={editable} multiline
                onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
                style={{
                  position: "absolute", left: px(bodyX + col * cw + GEO.cellPadX), top: px(y + GEO.cellPadY),
                  width: px(cw - 2 * GEO.cellPadX), height: px(rowH - 2 * GEO.cellPadY),
                  fontSize: pt(skin.scale.caption), color: skin.text, fontFamily: skin.fontBody, lineHeight: 1.15,
                }}
              />
            ))}
          </div>
        );
      })}
      {/* marks：右上角 accent 方块 + tag */}
      {(b.marks ?? []).map((mk, i) => {
        if (mk.row < 0 || mk.row >= rows || mk.col < 0 || mk.col >= cols) return null;
        const y = GEO.tableTop + GEO.headH + mk.row * rowH;
        const x = bodyX + mk.col * cw;
        return (
          <div key={`mk${i}`}>
            <div style={{
              position: "absolute", left: px(x + cw - GEO.markSize - GEO.cellPadX / 2), top: px(y + 0.05),
              width: px(GEO.markSize), height: px(GEO.markSize), background: skin.accent,
            }} />
            {mk.tag && (
              <div style={{
                position: "absolute", left: px(x + cw - GEO.tagW - GEO.cellPadX / 2), top: px(y + 0.05 + GEO.markSize),
                width: px(GEO.tagW), height: px(GEO.tagH), textAlign: "right",
                fontSize: pt(skin.scale.meta), color: skin.accent, fontFamily: skin.fontBody, fontWeight: 700,
              }}>{mk.tag}</div>
            )}
          </div>
        );
      })}
    </>
  );
}
